/**
 * Custom hook for fetching Google Drive folders for Outreach Templates
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DriveFolder, OutreachTemplatesResponse } from '../types/outreachTemplates';
import { buildApiUrl } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from "../utils/apiClient";

interface UseDriveFoldersReturn {
  folders: DriveFolder[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const CACHE_KEY = 'outreach_templates_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

interface CachedData {
  data: DriveFolder[];
  timestamp: number;
}

/**
 * Hook for fetching and managing Google Drive folders
 * @returns Folders data, loading state, error state, and refetch function
 */
export const useDriveFolders = (): UseDriveFoldersReturn => {
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const loadFolders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cachedDataStr = sessionStorage.getItem(CACHE_KEY);
      if (cachedDataStr) {
        const cachedData: CachedData = JSON.parse(cachedDataStr);
        const now = Date.now();

        // Use cached data if still valid
        if (now - cachedData.timestamp < CACHE_DURATION) {
          setFolders(cachedData.data);
          setLoading(false);
          return;
        }
      }

      // Check authentication via AuthContext
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      const response = await apiClient(
        buildApiUrl('/api/drive/outreach-templates'),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Send HTTP-only cookies
        }
      );

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (response.status === 403) {
        setError('Google Drive access not authorized. Please reconnect your Google account.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load templates (Status: ${response.status})`);
      }

      const data: OutreachTemplatesResponse = await response.json();
      setFolders(data.folders || []);

      // Cache the data
      const cacheData: CachedData = {
        data: data.folders || [],
        timestamp: Date.now(),
      };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load templates';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const refetch = useCallback(() => {
    // Clear cache and refetch
    sessionStorage.removeItem(CACHE_KEY);
    loadFolders();
  }, [loadFolders]);

  return {
    folders,
    loading,
    error,
    refetch,
  };
};

